import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NewsPreferences } from "@/hooks/useNewsPreferences";

interface TopicsTabProps {
  preferences: NewsPreferences;
  onChange: (updates: Partial<NewsPreferences>) => void;
  keywordsInput: string;
  onKeywordsInputChange: (value: string) => void;
}

const AVAILABLE_CATEGORIES = [
  { id: 'industry_trends', label: 'Industry Trends' },
  { id: 'technology', label: 'Technology Innovations' },
  { id: 'regulations', label: 'Regulations & Compliance' },
  { id: 'safety', label: 'Safety & Risk Management' },
  { id: 'sustainability', label: 'Sustainability & ESG' },
  { id: 'company_news', label: 'Company News' },
  { id: 'market_analysis', label: 'Market Analysis' },
  { id: 'supply_chain', label: 'Supply Chain Updates' },
  { id: 'labor', label: 'Labor & Workforce' },
  { id: 'equipment', label: 'Equipment & Materials' },
];

const INDUSTRY_FOCUS_OPTIONS = [
  'General Construction',
  'Commercial Construction',
  'Residential Construction',
  'Infrastructure & Civil Engineering',
  'Specialty Trade Contractor',
  'Real Estate Development',
];

export const TopicsTab = ({ preferences, onChange, keywordsInput, onKeywordsInputChange }: TopicsTabProps) => {
  const toggleCategory = (categoryId: string) => {
    const current = preferences.enabled_categories || [];
    const updated = current.includes(categoryId)
      ? current.filter(c => c !== categoryId)
      : [...current, categoryId];
    onChange({ enabled_categories: updated });
  };

  const keywordCount = keywordsInput
    .split(',')
    .map(k => k.trim())
    .filter(k => k.length > 0)
    .length;

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label className="text-base font-semibold">News Categories</Label>
          <p className="text-sm text-muted-foreground mb-3">
            Select which types of news articles you want to see
          </p>
          <div className="grid grid-cols-2 gap-3">
            {AVAILABLE_CATEGORIES.map((category) => (
              <div key={category.id} className="flex items-center space-x-2">
                <Checkbox
                  id={category.id}
                  checked={preferences.enabled_categories?.includes(category.id)}
                  onCheckedChange={() => toggleCategory(category.id)}
                />
                <Label
                  htmlFor={category.id}
                  className="text-sm font-normal cursor-pointer"
                >
                  {category.label}
                </Label>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <Label className="text-base font-semibold">Industry Focus</Label>
          <p className="text-sm text-muted-foreground">
            Your primary construction industry segment
          </p>
          <Select
            value={preferences.industry_focus}
            onValueChange={(value) => onChange({ industry_focus: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INDUSTRY_FOCUS_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          <Label className="text-base font-semibold">Custom Keywords</Label>
          <p className="text-sm text-muted-foreground">
            Add specific keywords to track (comma-separated, max 20)
          </p>
          <Textarea
            value={keywordsInput}
            onChange={(e) => onKeywordsInputChange(e.target.value)}
            placeholder="e.g., prefab, modular construction, BIM, safety compliance"
            rows={3}
            className="resize-none"
          />
          <p className="text-xs text-muted-foreground">
            Keywords: {keywordCount} / 20
          </p>
        </div>
      </div>
    </div>
  );
};
